import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import avo from 'analytics/avo';
import {
  getTalkativeCustomerId,
  getUserFromCookie,
} from 'commons/localStorage';
import Radio from 'components/essentials/Radio';
import CtaButton from 'components/essentials/Cta/CtaButton';
import { SUBMIT_BUSINESS_APPLICATION } from 'mocks/mutations';
import LabeledTextarea from 'components/consumer/LabeledTextarea';
import LabeledTextInput from 'components/consumer/LabeledTextInput';
import ZipField from 'components/consumer/checkout/desktop/components/Shipping/components/ZipField';
import CityField from 'components/consumer/checkout/desktop/components/Shipping/components/CityField';
import StateField from 'components/consumer/checkout/desktop/components/Shipping/components/StateField';
import EmailField from 'components/consumer/checkout/desktop/components/EmailForm/components/EmailField';
import LastNameField from 'components/consumer/checkout/desktop/components/Shipping/components/LastNameField';
import ApartmentField from 'components/consumer/checkout/desktop/components/Shipping/components/ApartmentField';
import FirstNameField from 'components/consumer/checkout/desktop/components/Shipping/components/FirstNameField';
import PhoneNumberField from 'components/consumer/checkout/desktop/components/Shipping/components/PhoneNumberField';
import NewsletterOptIn from 'components/consumer/NewsletterOptIn';
import TwoColumnFormFields from 'components/consumer/TwoColumnFormFields';
import isClient from 'commons/isClient';
import {
  INVALID_NAME_CHARACTERS_REGEX,
  EMAIL_REGEX,
  PHONE_REGEX,
  ZIP_CODE_REGEX,
} from 'commons/constants';
import { sendFBEvent, FB_EVENTS } from 'commons/fbEvents';
import ValidationError from 'components/consumer/ValidationError';
import withApolloClient from 'components/hoc/withApolloClient';
import urlAffiliateFields from 'commons/urlAffiliateFields';

const INITIAL_VALUES = {
  designer_profession: '',
  designer_website: '',
  designer_title: '',
  designer_first_name: '',
  designer_last_name: '',
  designer_company: '',
  designer_email: '',
  designer_mailing: '',
  designer_mailing_2: '',
  designer_city: '',
  designer_state: '',
  designer_zip: '',
  designer_phone: '',
  designer_organizations: '',
  designer_organizations_other: '',
  designer_interests: '',
  designer_interests_other: '',
  store_sq_footage: '',
  yearly_upholst_sale: '',
  newsletter_opt_in: false,
};

const formSchema = Yup.object().shape({
  designer_profession: Yup.string().required('Required'),
  designer_first_name: Yup.string()
    .required('Required')
    .test(
      'test-fname',
      'No invalid characters allowed',
      value => !INVALID_NAME_CHARACTERS_REGEX.test(value)
    ),
  designer_last_name: Yup.string()
    .required('Required')
    .test(
      'test-lname',
      'No invalid characters allowed',
      value => !INVALID_NAME_CHARACTERS_REGEX.test(value)
    ),
  designer_email: Yup.string()
    .required('Required')
    .matches(EMAIL_REGEX, 'Invalid email'),
  designer_phone: Yup.string()
    .required('Required')
    .matches(PHONE_REGEX, 'Invalid phone'),
  designer_company: Yup.string().required('Required'),
  designer_website: Yup.string().required('Required'),
  designer_mailing: Yup.string().required('Required'),
  designer_city: Yup.string().required('Required'),
  designer_state: Yup.string().required('Required'),
  designer_zip: Yup.string()
    .required('Required')
    .matches(ZIP_CODE_REGEX, 'Invalid zipcode'),
  designer_interests_other: Yup.string().required('Required'),
});

const professions = [
  // 'Store Owner/Retailer',
  'Interior Designer',
  'Design Firm/Organization',
  'Architect',
  'Hospitality',
  'Specifier/Contract Buyer',
  'Other',
];

const memberships = [
  'ASID (American Society of Interior Designers or international affiliate)',
  'AIA (American Institute of Architects)',
  'CID (Certified Interior Decorators International)',
  'IIDA (International Interior Design Association)',
  'NCDIQ (National Council for Interior Design Qualifacation)',
  'AIDP (Association of Interior Design Professionals)',
  'Other, please explain how you are in the trade:',
];

const interests = ['Upholstery', 'Case Goods', 'Other, please explain below'];

const ConsumerBusinessTradeFormSuccess = () => (
  <div className="flex items-center justify-center flex-col min-h-[40rem]">
    <h2 className="text-3xl font-bold">Thanks for your interest!</h2>
    <br />
    <div className="consumer-business-trade-form__subtitle">
      Your trade information has been submitted and will be reviewed shortly.
      Applications are usually reviewed within 24 - 48 hours.
    </div>
  </div>
);

class ConsumerBusinessTradeForm extends React.Component {
  state = {
    saved: false,
  };

  containerRef = React.createRef();

  componentDidMount() {
    if (this.props.isAdmin) {
      return;
    }

    if (
      isClient() &&
      document.location.hash.includes('#consumer-business-trade-form')
    ) {
      setTimeout(() => {
        // scroll to horizontal spacing
        this.containerRef.current.previousSibling.scrollIntoView();
      }, 2000);
    }
    if (isClient()) {
      const user = getUserFromCookie();
      avo.tradeApplicationVisited({
        ...urlAffiliateFields(),
        userId_: user?.id?.toString() ?? '',
        email: user?.email ?? '',
        backendEmail: user?.email ?? '',
      });
    }
  }

  renderForm = () => (
    <>
      <h2 className="text-3xl font-bold">
        Apply Now for Trade and Business Approval
      </h2>
      <div className="text-base">
        If you’re an architect, designer, developer, or retailer, enjoy a trade
        program that gives you unparalleled access to source Joybird pieces at
        great prices.
      </div>
      <Formik
        initialValues={INITIAL_VALUES}
        validationSchema={formSchema}
        onSubmit={async (values, actions) => {
          avo.ctaClicked({
            cta: 'Submit Trade Application',
            targetUrl: '',
            text: 'Apply',
            component: avo.Component.TRADE_NEWSLETTER_SIGNUP,
            location: window.location.pathname.includes('/business')
              ? 'Business'
              : 'Trade Application',
          });
          const formValues = {
            // data
            designer_first_name: values.designer_first_name,
            designer_last_name: values.designer_last_name,
            designer_email: values.designer_email,
            designer_phone: values.designer_phone,
            designer_mailing: values.designer_mailing,
            designer_mailing_2: values.designer_mailing_2,
            designer_city: values.designer_city,
            designer_state: values.designer_state,
            designer_zip: values.designer_zip,
            designer_profession: values.designer_profession,
            profession_other: values.profession_other,
            designer_company: values.designer_company,
            designer_website: values.designer_website,
            designer_title: values.designer_title,
            designer_organizations: values.designer_organizations,
            designer_organizations_other: values.designer_organizations_other,
            designer_interests: values.designer_interests,
            designer_interests_other: values.designer_interests_other,
            store_sq_footage: values.store_sq_footage,
            yearly_upholst_sale: values.yearly_upholst_sale,
            refer_origin: window.document.referrer,
            // remove leading slash (^\/) or (|) trailing slash (\/$)
            request_page: window.location.pathname.replace(/^\/|\/$/g, ''),
          };
          interests.forEach(interest => {
            if (values[interest]) {
              if (!formValues.designer_interests) {
                formValues.designer_interests = [];
              }
              formValues.designer_interests.push(interest);
            }
          });
          memberships.forEach(membership => {
            if (values[membership]) {
              if (!formValues.designer_organizations) {
                formValues.designer_organizations = [];
              }
              formValues.designer_organizations.push(membership);
            }
          });
          try {
            actions.setFieldError('formErrors', '');
            const res = await this.props.client.mutate({
              mutation: SUBMIT_BUSINESS_APPLICATION,
              variables: {
                form: formValues,
              },
            });
            if (res.data.businessRequest.success) {
              const user = getUserFromCookie();
              avo.tradeApplicationSubmitted({
                ...urlAffiliateFields(),
                userId_: user?.id?.toString() ?? '',
                email: values.designer_email,
                backendEmail: values.designer_email,
                optedInTrade: values.newsletter_opt_in,
              });
              avo.emailAcquired({
                userId_: '',
                email: values.designer_email,
                optedInMarketing: values.newsletter_opt_in,
                location: avo.Location.HOME_PAGE,
                backendEmail: values.designer_email,
                component: avo.Component.TRADE_NEWSLETTER_SIGNUP,
                talkativeCustomerId: getTalkativeCustomerId(),
              });
              this.setState({ saved: true }, () => {
                this.containerRef.current.scrollIntoView();
              });
              sendFBEvent(FB_EVENTS.LEAD, {
                content_name: 'TradeSignup',
              });
            } else {
              actions.setFieldError(
                'formErrors',
                res.data.businessRequest.message ||
                  'Unable to submit application'
              );
            }
          } catch (e) {
            actions.setFieldError('formErrors', 'Unable to submit application');
          } finally {
            actions.setSubmitting(false);
          }
        }}
        render={({
          values,
          errors,
          touched,
          handleSubmit,
          handleBlur,
          handleChange,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className="mt-10 mx-0 mb-16">
            <p className="text-sm text-gray-light1 m-0">
              * denotes mandatory fields
            </p>

            <h3 className="text-lg font-bold pb-1 mt-9 mx-0 mb-[.825rem] [border-bottom:1px_solid_#e5e5e5]">
              I work in/as: *
            </h3>

            {professions.map(item => (
              <Radio
                key={item}
                name="designer_profession"
                onChange={() => {
                  setFieldValue('designer_profession', item);
                }}
                checked={values.designer_profession === item}
              >
                <span className="text-sm font-bold">{item}</span>
              </Radio>
            ))}
            {touched.designer_profession && errors.designer_profession && (
              <ValidationError block>
                {errors.designer_profession}
              </ValidationError>
            )}
            {values.designer_profession === 'Other' && (
              <div className="mb-4">
                <LabeledTextInput
                  id="profession_other"
                  name="profession_other"
                  data-xc="profession_other"
                  handleBlur={handleBlur}
                  onChange={handleChange}
                  label="Explain your profession"
                  value={values.profession_other}
                  errorMessage={
                    touched.profession_other && errors.profession_other
                  }
                />
              </div>
            )}

            <div className="mb-16">
              <h3 className="text-lg font-bold pb-1 mt-9 mx-0 mb-[.825rem] [border-bottom:1px_solid_#e5e5e5]">
                Personal information:
              </h3>

              <EmailField
                label="Email *"
                id="designer_email"
                name="designer_email"
                autoComplete="email"
                data-xc="designer_email"
                handleBlur={handleBlur}
                handleChange={handleChange}
                value={values.designer_email}
                error={errors.designer_email}
                isTouched={touched.designer_email}
              />

              <TwoColumnFormFields>
                <FirstNameField
                  id="designer_first_name"
                  name="designer_first_name"
                  data-xc="designer_first_name"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  error={errors.designer_first_name}
                  value={values.designer_first_name}
                  isTouched={touched.designer_first_name}
                />

                <LastNameField
                  id="designer_last_name"
                  name="designer_last_name"
                  data-xc="designer_last_name"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  error={errors.designer_last_name}
                  value={values.designer_last_name}
                  isTouched={touched.designer_last_name}
                />
              </TwoColumnFormFields>

              <LabeledTextInput
                label="Title"
                id="designer_title"
                autoComplete="honorific-prefix"
                name="designer_title"
                data-xc="designer_title"
                handleBlur={handleBlur}
                onChange={handleChange}
                error={errors.designer_title}
                value={values.designer_title}
                isTouched={touched.designer_title}
              />

              <TwoColumnFormFields>
                <LabeledTextInput
                  label="Company *"
                  id="designer_company"
                  autoComplete="organization"
                  name="designer_company"
                  data-xc="designer_company"
                  handleBlur={handleBlur}
                  onChange={handleChange}
                  value={values.designer_company}
                  errorMessage={
                    touched.designer_company && errors.designer_company
                  }
                />

                <LabeledTextInput
                  label="Website *"
                  id="designer_website"
                  autoComplete="url"
                  name="designer_website"
                  data-xc="designer_website"
                  handleBlur={handleBlur}
                  onChange={handleChange}
                  value={values.designer_website}
                  errorMessage={
                    touched.designer_website && errors.designer_website
                  }
                />
              </TwoColumnFormFields>

              <LabeledTextInput
                label="Address *"
                id="designer_mailing"
                name="designer_mailing"
                autoComplete="address-line1"
                data-xc="designer_mailing"
                handleBlur={handleBlur}
                onChange={handleChange}
                value={values.designer_mailing}
                errorMessage={
                  touched.designer_mailing && errors.designer_mailing
                }
              />

              <TwoColumnFormFields>
                <ApartmentField
                  showError
                  id="designer_mailing_2"
                  name="designer_mailing_2"
                  data-xc="designer_mailing_2"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  placeholder="Apartment / Suite #"
                  value={values.designer_mailing_2}
                  error={errors.designer_mailing_2}
                  isTouched={touched.designer_mailing_2}
                />

                <CityField
                  id="designer_city"
                  name="designer_city"
                  data-xc="designer_city"
                  value={values.designer_city}
                  error={errors.designer_city}
                  isTouched={touched.designer_city}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                />
              </TwoColumnFormFields>

              <TwoColumnFormFields>
                <StateField
                  id="designer_state"
                  name="designer_state"
                  data-xc="designer_state"
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  value={values.designer_state}
                  error={errors.designer_state}
                  isTouched={touched.designer_state}
                  setFieldTouched={setFieldTouched}
                />

                <ZipField
                  id="designer_zip"
                  name="designer_zip"
                  data-xc="designer_zip"
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  value={values.designer_zip}
                  error={errors.designer_zip}
                  isTouched={touched.designer_zip}
                />
              </TwoColumnFormFields>

              <PhoneNumberField
                id="designer_phone"
                name="designer_phone"
                data-xc="designer_phone"
                placeholder="Phone *"
                handleBlur={handleBlur}
                handleChange={handleChange}
                error={errors.designer_phone}
                value={values.designer_phone}
                isTouched={touched.designer_phone}
              />
            </div>

            <h3 className="text-lg font-bold pb-1 mt-9 mx-0 mb-[.825rem] [border-bottom:1px_solid_#e5e5e5]">
              Tell us about your Business/Project:
            </h3>

            <div className="mb-4">
              <LabeledTextarea
                label="Message *"
                name="designer_interests_other"
                data-xc="designer_interests_other"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.designer_interests_other}
                errorMessage={
                  touched.designer_interests_other &&
                  errors.designer_interests_other
                }
                maxLength={10000}
                autoComplete="off"
                rows={4}
              />
            </div>
            <div>
              <NewsletterOptIn
                onClick={isChecked => {
                  setFieldValue('newsletter_opt_in', isChecked);
                }}
                checked={values.newsletter_opt_in}
              />
            </div>
            {errors.formErrors && (
              <ValidationError>{errors.formErrors}</ValidationError>
            )}
            <CtaButton
              type="submit"
              color="black"
              styling="solid-button"
              data-xc="business-form-button"
              disabled={isSubmitting}
              block
              isLoading={isSubmitting}
            >
              Apply
            </CtaButton>
          </form>
        )}
      />
    </>
  );

  render() {
    if (this.props?.isAdmin) {
      return (
        <div>
          <h1>Consumer Business Trade Form</h1>
        </div>
      );
    }
    const divId = this.props?.config?.elementAttribute?.id
      ? this.props?.config?.elementAttribute?.id
      : 'consumer-business-trade-form';
    return (
      <div
        ref={this.containerRef}
        id={divId}
        data-xc="consumer-business-trade-form"
        className="max-w-[35rem] mx-auto my-8 max-[575px]:max-w-[85%]"
      >
        {this.state.saved ? (
          <ConsumerBusinessTradeFormSuccess />
        ) : (
          this.renderForm()
        )}
      </div>
    );
  }
}

ConsumerBusinessTradeForm.propTypes = {
  client: PropTypes.shape({
    mutate: PropTypes.func,
  }),
  isAdmin: PropTypes.bool,
  config: PropTypes.shape({
    elementAttribute: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

export default withApolloClient(ConsumerBusinessTradeForm);
